import { dateComparator } from '@web-app/utils/sortComparators.utils';
import { trpcClient } from '@web-app/utils/trpc';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';

export const MAX_ACTIVE_GOALS = 3;

// ============================================================================
// PRIMITIVE ATOMS - Sources of truth
// ============================================================================

/** Patient ID for fetching goals */
export const sessionGoalsPatientIdAtom = atom<string | null>(null);

/** Current appointment ID (for linking new goals to appointment) */
export const sessionGoalsAppointmentIdAtom = atom<string | undefined>(
  undefined,
);

// ============================================================================
// SERVER DATA ATOMS - tRPC integration
// ============================================================================

/** Fetch goals from server via tRPC */
export const sessionGoalsQueryAtom = atomWithQuery((get) => {
  const patientId = get(sessionGoalsPatientIdAtom);

  return {
    queryKey: ['sessionGoals', 'byPatientId', patientId],
    queryFn: async () => {
      if (!patientId) return [];
      return trpcClient.sessionGoals.getByPatientId.query({
        patientId,
        includeArchived: true,
      });
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  };
});

/** Extract goals array from query result */
export const goalsAtom = atom((get) => {
  const queryResult = get(sessionGoalsQueryAtom);
  return queryResult.data ?? [];
});

/** Active (non-archived) goals, sorted by creation date ascending (oldest first) */
export const activeGoalsAtom = atom((get) => {
  const goals = get(goalsAtom);
  return goals
    .filter((g) => g.isActive && !g.archivedAt)
    .sort(dateComparator('ASC', 'createdAt'));
});

/** Archived goals, sorted by archive date descending (most recent first) */
export const archivedGoalsAtom = atom((get) => {
  const goals = get(goalsAtom);
  return goals
    .filter((g) => g.archivedAt !== null && g.archivedAt !== undefined)
    .sort(dateComparator('DESC', 'archivedAt'));
});

/** Loading state from query */
export const isLoadingAtom = atom((get) => {
  const queryResult = get(sessionGoalsQueryAtom);
  return queryResult.status === 'pending';
});

/** Refetch query atom (for invalidation after mutations) */
export const refetchGoalsAtom = atom((get) => {
  const queryResult = get(sessionGoalsQueryAtom);
  return queryResult.refetch;
});

// ============================================================================
// MUTATION ATOMS - Immediate server operations
// ============================================================================

/** Create a new goal immediately */
export const createGoalMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['sessionGoals', 'create'],
  mutationFn: async ({
    content,
    patientId,
    appointmentId,
  }: {
    content: string;
    patientId: string;
    appointmentId?: string;
  }) => {
    return trpcClient.sessionGoals.upsert.mutate({
      patientId,
      content: content.trim(),
      appointmentId,
    });
  },
  onSuccess: () => {
    const refetch = get(refetchGoalsAtom);
    refetch();
  },
}));

/** Archive a goal immediately */
export const archiveGoalMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['sessionGoals', 'archive'],
  mutationFn: async ({ id, patientId }: { id: string; patientId: string }) => {
    return trpcClient.sessionGoals.upsert.mutate({
      id,
      patientId,
      archive: true,
    });
  },
  onSuccess: () => {
    const refetch = get(refetchGoalsAtom);
    refetch();
  },
}));

/** Update a goal's content immediately */
export const updateGoalMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['sessionGoals', 'update'],
  mutationFn: async ({
    id,
    content,
    patientId,
  }: {
    id: string;
    content: string;
    patientId: string;
  }) => {
    return trpcClient.sessionGoals.upsert.mutate({
      id,
      patientId,
      content: content.trim(),
    });
  },
  onSuccess: () => {
    const refetch = get(refetchGoalsAtom);
    refetch();
  },
}));

// ============================================================================
// ANALYTICS ATOMS - Tracking state for session goals
// ============================================================================

interface GoalsAnalytics {
  /** Unique ID generated when component loads, included in all tracking events */
  loadId: string;
  /** Whether the view event has already been tracked (prevents duplicates) */
  hasTrackedView: boolean;
}

/** Analytics tracking state for session goals */
export const goalsAnalyticsAtom = atom<GoalsAnalytics>({
  loadId: '',
  hasTrackedView: false,
});
